import { useState } from "react"
import type { FC } from "react"
import { useNavigate } from "react-router-dom"

import { useMutation, useQuery } from "@apollo/client"

import styles from "./GeneralInfoTab.module.scss"
import HubspotContractsSection from "./HubspotContractsSection/HubspotContractsSection"
import LineGroupMappingsSection from "./LineGroupMappingsSection/LineGroupMappingsSection"

import { useSwitchSalonForStoreManagement } from "@/lib/graphql/useSwitchSalon"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"

const GetCompany = graphql(/* GraphQL */ `
	query GetCompanyForGeneralInfo($param: CompanyParam!) {
		company(param: $param) {
			company {
				id
				name
				salons {
					id
					name
					displayName
				}
				lineGroups {
					id
					name
				}
				hubspotContracts {
					...HubspotContractsSection_HubspotContract
				}
			}
		}
	}
`)

const GenerateFinLoginUrl = graphql(/* GraphQL */ `
	mutation GenerateFinLoginUrlForGeneralInfoTab(
		$input: GenerateFinLoginUrlInput!
	) {
		generateFinLoginUrl(input: $input) {
			url
		}
	}
`)

type GeneralInfoTabProps = {
	companyId: string
}

const GeneralInfoTab: FC<GeneralInfoTabProps> = ({ companyId }) => {
	const navigate = useNavigate()
	const switchSalon = useSwitchSalonForStoreManagement()
	const [generatedCompanyUrl, setGeneratedCompanyUrl] = useState<{
		url: string
	} | null>(null)
	const [copiedUrl, setCopiedUrl] = useState<string | null>(null)
	const [copiedSalonIds, setCopiedSalonIds] = useState(false)

	const { data, loading } = useQuery(GetCompany, {
		variables: {
			param: {
				id: companyId,
			},
		},
		fetchPolicy: "cache-first",
	})
	const [generateFinLoginUrl, { loading: generatingCompanyUrl }] =
		useMutation(GenerateFinLoginUrl)

	const company = data?.company.company

	const handleEdit = () => {
		navigate(`/souhl_admin/companies/${companyId}/edit`)
	}

	const handleGenerateCompanyUrl = async () => {
		try {
			const { data } = await generateFinLoginUrl({
				variables: {
					input: { companyId },
				},
			})
			const result = data?.generateFinLoginUrl
			if (!result) return

			setGeneratedCompanyUrl({
				url: result.url,
			})
		} catch (error) {
			console.error("URL生成エラー:", error)
		}
	}

	const handleCopyUrl = async (url: string) => {
		await navigator.clipboard.writeText(url)
		setCopiedUrl(url)
		setTimeout(() => setCopiedUrl(null), 2000)
	}

	const handleCopySalonIds = async (salonIds: number[]) => {
		await navigator.clipboard.writeText(salonIds.join(", "))
		setCopiedSalonIds(true)
		setTimeout(() => setCopiedSalonIds(false), 2000)
	}

	const handleGoToHome = (salonId: number) => {
		// 対象サロンを選択（SalonContext をサーバーサイドで同期）してから Home 画面へ移動
		switchSalon(salonId)
			.then(() => {
				window.location.href = "/"
			})
			.catch((error) => {
				console.error("店舗の切り替えに失敗しました", error)
			})
	}

	if (loading && !data) {
		return (
			<div className={styles.loadingContainer}>
				<div className={styles.loading}>読み込み中...</div>
			</div>
		)
	}

	if (!company) {
		return <div className={styles.error}>会社が見つかりませんでした</div>
	}

	return (
		<div className={styles.generalInfoTab}>
			<div className={styles.formContainer}>
				<div className={styles.formHeader}>
					<h2 className={styles.formTitle}>会社情報</h2>
					<div className={styles.editActions}>
						<button
							type="button"
							className={styles.generateButton}
							onClick={handleGenerateCompanyUrl}
							disabled={generatingCompanyUrl}
						>
							{generatingCompanyUrl ? "生成中..." : "Fin URL 生成"}
						</button>
						<button
							type="button"
							className={styles.editButton}
							onClick={handleEdit}
						>
							編集
						</button>
					</div>
				</div>

				<div className={styles.formContent}>
					<div className={styles.formField}>
						<label className={styles.label}>ID</label>
						<div className={styles.value}>{company.id}</div>
					</div>
					<div className={styles.formField}>
						<label className={styles.label}>名前</label>
						<div className={styles.value}>{company.name}</div>
					</div>
				</div>

				{generatedCompanyUrl && (
					<div className={styles.generatedUrlsSection}>
						<div className={styles.generatedUrlItem}>
							<span className={styles.generatedUrlText}>
								{generatedCompanyUrl.url}
							</span>
							<button
								type="button"
								className={styles.copyButton}
								onClick={async () =>
									await handleCopyUrl(generatedCompanyUrl.url)
								}
							>
								{copiedUrl === generatedCompanyUrl.url
									? "コピー済み"
									: "コピー"}
							</button>
						</div>
					</div>
				)}
			</div>

			<LineGroupMappingsSection
				companyId={companyId}
				lineGroups={company.lineGroups}
			/>

			<HubspotContractsSection
				companyId={companyId}
				contracts={company.hubspotContracts}
			/>

			<div className={styles.formContainer}>
				<div className={styles.formHeader}>
					<h2 className={styles.formTitle}>
						紐付けサロン ({company.salons.length}件)
					</h2>
					<button
						type="button"
						className={styles.generateButton}
						onClick={async () =>
							await handleCopySalonIds(company.salons.map((s) => s.id))
						}
						disabled={company.salons.length === 0}
					>
						{copiedSalonIds ? "コピー済み" : "サロンID をコピー"}
					</button>
				</div>

				<div className={styles.salonList}>
					{company.salons.length === 0 ? (
						<div className={styles.emptyMessage}>
							紐付けられているサロンはありません
						</div>
					) : (
						<ul className={styles.salons}>
							{company.salons.map((salon) => (
								<li key={salon.id} className={styles.salonItem}>
									<span className={styles.salonItemLabel}>
										【{salon.id}】{salon.name} ({salon.displayName})
									</span>
									<button
										type="button"
										className={styles.generateButton}
										onClick={() => handleGoToHome(salon.id)}
									>
										Home画面に移動
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
			</div>
		</div>
	)
}

export default GeneralInfoTab
